import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip, Button,
  Checkbox, Tooltip, Radio, RadioGroup, FormControlLabel, FormGroup, Divider,
  Select, MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import ApiService from '../../functions/apiService';
import AuthContext from '../../context/AuthContext';
import { toast } from 'react-toastify';
import moment from 'moment';

const getDefaultPeriodDate = () => {
  const today = new Date();
  const thisYearJuly1 = new Date(today.getFullYear(), 6, 1);
  return today >= thisYearJuly1
    ? `${today.getFullYear()}-07-01`
    : `${today.getFullYear() - 1}-07-01`;
};

const DrilldownDialog = ({ drilldown, onClose }) => (
  <Dialog open={!!drilldown} onClose={onClose} fullWidth maxWidth="xs"
    PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
    <DialogTitle sx={{
      backgroundColor: '#BF1D2D',
      color: '#fff',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      py: 1.5,
      px: 2.5,
    }}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {drilldown?.type} — {drilldown?.name}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.85 }}>
          {drilldown?.list?.length} {drilldown?.list?.length === 1 ? 'entry' : 'entries'}
          {' · '}
          {drilldown?.list?.reduce((s, i) => s + Number(i.hours), 0)} total hrs
        </Typography>
      </Box>
      <IconButton size="small" sx={{ color: '#fff', mt: -0.25 }} onClick={onClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ p: 0, maxHeight: 340, overflow: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50', position: 'sticky', top: 0, zIndex: 1 }}>
            <TableCell sx={{ fontWeight: 600, pl: 2.5, py: 1.25, borderBottom: '2px solid', borderColor: 'grey.200' }}>
              Date
            </TableCell>
            {drilldown?.list?.[0]?.shift !== undefined && (
              <TableCell sx={{ fontWeight: 600, py: 1.25, borderBottom: '2px solid', borderColor: 'grey.200' }}>
                Shift
              </TableCell>
            )}
            <TableCell align="right" sx={{ fontWeight: 600, pr: 2.5, py: 1.25, borderBottom: '2px solid', borderColor: 'grey.200' }}>
              Hours
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {drilldown?.list?.map((item, i) => (
            <TableRow key={i} hover>
              <TableCell sx={{ pl: 2.5, py: 1 }}>{moment(item.date).format('MM/DD/YYYY')}</TableCell>
              {item.shift !== undefined && (
                <TableCell sx={{ py: 1 }}>
                  <Chip
                    label={item.shift}
                    size="small"
                    sx={{
                      fontSize: '11px',
                      height: 22,
                      backgroundColor: item.shift === 'Day' ? 'rgba(52,152,219,0.15)' : 'rgba(255,165,0,0.22)',
                      border: `1px solid ${item.shift === 'Day' ? 'rgba(52,152,219,0.3)' : 'rgba(255,165,0,0.35)'}`,
                      color: item.shift === 'Day' ? '#1565c0' : '#e65100',
                      fontWeight: 500,
                    }}
                  />
                </TableCell>
              )}
              <TableCell align="right" sx={{ pr: 2.5, py: 1 }}>{item.hours}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </DialogContent>
    <Box sx={{ backgroundColor: 'grey.50', borderTop: '2px solid', borderColor: 'grey.200', px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>Total</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {drilldown?.list?.reduce((s, i) => s + Number(i.hours), 0)} hrs
      </Typography>
    </Box>
  </Dialog>
);

const ClickableCell = ({ value, list, type, name, onDrilldown }) => {
  if (!list?.length) return value ?? 0;
  return (
    <span
      style={{ cursor: 'pointer', color: '#BF1D2D', textDecoration: 'underline', fontWeight: 500 }}
      onClick={() => onDrilldown({ type, name, list })}
    >
      {value}
    </span>
  );
};

const RADIO_SX = { color: '#BF1D2D', '&.Mui-checked': { color: '#BF1D2D' } };
const CHECK_SX = { color: '#BF1D2D', '&.Mui-checked': { color: '#BF1D2D' }, '&.MuiCheckbox-indeterminate': { color: '#BF1D2D' } };

const ResetAccrualDialog = ({ open, onClose, subTypes, onSubmit, submitting }) => {
  const [step, setStep] = useState(1);
  const [action, setAction] = useState('');
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [periodDate, setPeriodDate] = useState(getDefaultPeriodDate());
  const [periodStartValue, setPeriodStartValue] = useState(0);

  useEffect(() => {
    if (open) {
      setStep(1);
      setAction('');
      setSelectedCodes([]);
      setPeriodDate(getDefaultPeriodDate());
      setPeriodStartValue(0);
    }
  }, [open]);

  const totalSteps = action === 'NEW_PERIOD' ? 3 : 2;
  const isLastStep = (step === 2 && action === 'HARD_RESET') || step === 3;

  const stepLabel = step === 1
    ? 'Choose Action'
    : step === 2
      ? 'Select Absent Codes'
      : 'Set Period Start Date';

  const canProceed =
    (step === 1 && !!action) ||
    (step === 2 && selectedCodes.length > 0) ||
    (step === 3 && !!periodDate);

  const allCodesSelected = subTypes.length > 0 && selectedCodes.length === subTypes.length;
  const someCodesSelected = selectedCodes.length > 0 && !allCodesSelected;

  const handleToggleAll = () => {
    setSelectedCodes(allCodesSelected ? [] : subTypes.map((t) => t.SO_Value));
  };

  const handleToggleCode = (val) => {
    setSelectedCodes((prev) =>
      prev.includes(val) ? prev.filter((c) => c !== val) : [...prev, val]
    );
  };

  const handleNext = () => {
    if (step === 1) { setStep(2); return; }
    if (step === 2) {
      if (action === 'NEW_PERIOD') { setStep(3); return; }
      onSubmit({ action, absent_codes: selectedCodes });
      return;
    }
    if (step === 3) {
      onSubmit({ action, absent_codes: selectedCodes, period_date: periodDate, period_start_value: Number(periodStartValue) });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      <DialogTitle sx={{
        backgroundColor: '#BF1D2D', color: '#fff',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        py: 1.5, px: 2.5,
      }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            Reset Accrual
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {step === 1 ? 'Step 1' : `Step ${step} of ${totalSteps}`} — {stepLabel}
          </Typography>
        </Box>
        <IconButton size="small" sx={{ color: '#fff', mt: -0.25 }} onClick={onClose} disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3, px: 3, minHeight: 180 }}>
        {step === 1 && (
          <RadioGroup value={action} onChange={(e) => setAction(e.target.value)}>
            <FormControlLabel
              value="NEW_PERIOD"
              control={<Radio sx={RADIO_SX} />}
              label={
                <Box sx={{ py: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'karla, sans-serif' }}>
                    Add new period start for fiscal year
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Creates or updates a PERIOD_START record (value 0) for a chosen date — existing history is not deleted
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, alignItems: 'flex-start' }}
            />
            <FormControlLabel
              value="HARD_RESET"
              disabled
              control={<Radio sx={RADIO_SX} />}
              label={
                <Box sx={{ py: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'karla, sans-serif' }}>
                    Delete all accruals &amp; reset to 0
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Backs up data, zeroes all balances, and removes ACCRUED / SOLD / USED records for the selected absent codes
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </RadioGroup>
        )}

        {step === 2 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontFamily: 'karla, sans-serif' }}>
              Select the absent code types to apply this action to:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={allCodesSelected}
                  indeterminate={someCodesSelected}
                  onChange={handleToggleAll}
                  sx={CHECK_SX}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'karla, sans-serif' }}>
                  Select All ({subTypes.length})
                </Typography>
              }
            />
            <Divider sx={{ my: 0.75 }} />
            <FormGroup>
              {subTypes.map((t) => (
                <FormControlLabel
                  key={t.SO_Value}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedCodes.includes(t.SO_Value)}
                      onChange={() => handleToggleCode(t.SO_Value)}
                      sx={CHECK_SX}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontFamily: 'karla, sans-serif' }}>
                      {t.SO_Desc}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        ({t.SO_Value})
                      </Typography>
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {step === 3 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, fontFamily: 'karla, sans-serif' }}>
              A PERIOD_START record will be created (or updated if one already exists for this date) for each selected absent code across all selected members.
            </Typography>
            <TextField
              label="New Period Start Date"
              type="date"
              value={periodDate}
              onChange={(e) => setPeriodDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: '2099-12-31' }}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Period Start Value"
              type="number"
              value={periodStartValue}
              onChange={(e) => setPeriodStartValue(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, step: 0.5 }}
              helperText="Starting balance for the new period (default 0)"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              {selectedCodes.length} absent code{selectedCodes.length !== 1 ? 's' : ''} selected
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
        {step > 1 && (
          <Button
            onClick={() => setStep((s) => s - 1)}
            disabled={submitting}
            sx={{ textTransform: 'none', fontFamily: 'karla, sans-serif', color: 'text.secondary' }}
          >
            ← Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!canProceed || submitting}
          sx={{
            backgroundColor: '#BF1D2D',
            textTransform: 'none',
            fontFamily: 'karla, sans-serif',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#a5182a', boxShadow: 'none' },
          }}
        >
          {submitting ? 'Processing…' : isLastStep ? 'Submit' : 'Next →'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MemberAccrualSummary = () => {
  const { v3Data } = useContext(AuthContext);
  const allowedVars = v3Data;

  const [employees, setEmployees] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  const [accrualSubTypes, setAccrualSubTypes] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [empLoading, setEmpLoading] = useState(true);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [summary, setSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [drilldown, setDrilldown] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [checkedCodes, setCheckedCodes] = useState([]);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  useEffect(() => {
    ApiService.get('/accrual-management/form-data')
      .then((res) => {
        const d = res?.data?.data || res?.data || {};
        setEmployees(d.employees || []);
        setSubTypes(d.sub_types || []);
        setAccrualSubTypes(d.accrual_sub_types || d.sub_types || []);
      })
      .finally(() => setEmpLoading(false));
  }, []);

  const filteredEmployees = useMemo(() => {
    if (!empSearch) return employees;
    const q = empSearch.toLowerCase();
    return employees.filter((e) =>
      `${e.First_name || ''} ${e.Last_name || ''}`.toLowerCase().includes(q) ||
      String(e.Badge_Number || '').includes(q)
    );
  }, [employees, empSearch]);

  const fetchSummary = async (empCode, periodDate, isNewEmployee = false) => {
    setSummaryLoading(true);
    try {
      // null = "Current Period" (no param → backend uses <= today)
      const params = periodDate ? { period_date: periodDate } : {};
      const res = await ApiService.get(`/getEmployeeLog/${empCode}`, params);
      setSummary(res?.data?.summary || []);
      if (isNewEmployee) {
        setAvailablePeriods(res?.data?.available_periods || []);
        setSelectedPeriod(null); // default = Current Period
      }
    } catch {
      setSummary([]);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleSelectEmployee = async (emp, force = false) => {
    if (!force && selectedEmp?.Emp_Code === emp.Emp_Code) return;
    setSelectedEmp(emp);
    setSummary([]);
    setAvailablePeriods([]);
    setSelectedPeriod(null);
    await fetchSummary(emp.Emp_Code, null, true);
  };

  const handlePeriodChange = (period) => {
    // empty string = "Current Period" sentinel from the Select
    const resolved = period === '' ? null : period;
    setSelectedPeriod(resolved);
    if (selectedEmp) fetchSummary(selectedEmp.Emp_Code, resolved, false);
  };

  const handleResetSubmit = async ({ action, absent_codes, period_date, period_start_value }) => {
    setReverting(true);
    try {
      const payload = { EmpId: checkedCodes, action, absent_codes };
      if (period_date) payload.period_date = period_date;
      if (period_start_value !== undefined) payload.period_start_value = period_start_value;
      const res = await ApiService.post('/revertEmployeeAccrual', payload);
      toast.success(res?.message || (action === 'NEW_PERIOD' ? 'New accrual period set successfully' : 'Accrual data reset processed'));
      if (selectedEmp && checkedCodes.includes(selectedEmp.Emp_Code)) {
        await fetchSummary(selectedEmp.Emp_Code, selectedPeriod, false);
      }
      setCheckedCodes([]);
      setResetDialogOpen(false);
    } catch (error) {
      toast.error(error?.data?.message || error?.response?.data?.message || 'Failed to process accrual reset');
    } finally {
      setReverting(false);
    }
  };

  const toggleChecked = (code) => {
    setCheckedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const filteredCodes = useMemo(() => filteredEmployees.map((e) => e.Emp_Code), [filteredEmployees]);
  const allChecked = filteredCodes.length > 0 && filteredCodes.every((c) => checkedCodes.includes(c));
  const someChecked = filteredCodes.some((c) => checkedCodes.includes(c));

  const toggleSelectAll = () => {
    setCheckedCodes((prev) => {
      if (allChecked) return prev.filter((c) => !filteredCodes.includes(c));
      return Array.from(new Set([...prev, ...filteredCodes]));
    });
  };

  const empLabel = (emp) => `${emp.First_name || ''} ${emp.Last_name || ''}`.trim();
  const canRevert = v3Data?.V3_RESET_ACCRUAL && v3Data?.V3_RESET_ACCRUAL == 'true';

  const hideSoldAccrued = allowedVars?.V3_HIDE_SOLD_AND_ACCRUED_IN_SUMMARY;
  const compressed = allowedVars && Object.keys(allowedVars).includes('V3_REMOVE_SELL_ACCRUAL_FEATURE');

  return (
    <Box sx={{ m: 2, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 90px)' }}>
      {/* Page title */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'karla, sans-serif', lineHeight: 1.2 }}>
            Member Accrual Summary
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'karla, sans-serif' }}>
            Review time off &amp; accrual balances for your members
          </Typography>
        </div>

        <div>
          {canRevert && (
            <Button
              variant="contained"
              size="small"
              disabled={checkedCodes.length === 0}
              onClick={() => setResetDialogOpen(true)}
              sx={{
                ml: 'auto',
                backgroundColor: '#BF1D2D',
                fontFamily: 'karla, sans-serif',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#a5182a', boxShadow: 'none' },
              }}
            >
              Reset Accrual
            </Button>
          )}
        </div>
      </Box>

      <Paper sx={{ display: 'flex', flex: 1, overflow: 'hidden', borderRadius: 2 }}>
        {/* Left: Employee list */}
        <Box sx={{
          width: 300,
          minWidth: 300,
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Search */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search members..."
              value={empSearch}
              onChange={(e) => setEmpSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Select-all toolbar */}
          {!empLoading && filteredEmployees.length > 0 && (
            <Box sx={{
              px: 1.5,
              py: 0.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: '#fafafa',
              display: 'flex',
              alignItems: 'center',
              minHeight: 44,
            }}>
              <Tooltip title={allChecked ? 'Deselect all' : 'Select all'}>
                <Checkbox
                  size="small"
                  checked={allChecked}
                  indeterminate={!allChecked && someChecked}
                  onChange={toggleSelectAll}
                  sx={{ color: '#BF1D2D', '&.Mui-checked': { color: '#BF1D2D' }, '&.MuiCheckbox-indeterminate': { color: '#BF1D2D' } }}
                />
              </Tooltip>
              <Typography variant="body2" sx={{ fontFamily: 'karla, sans-serif', color: 'text.secondary' }}>
                {checkedCodes.length > 0 ? `${checkedCodes.length} selected` : 'Select all'}
              </Typography>
              {canRevert && checkedCodes.length > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setResetDialogOpen(true)}
                  sx={{
                    ml: 'auto',
                    backgroundColor: '#BF1D2D',
                    fontFamily: 'karla, sans-serif',
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#a5182a', boxShadow: 'none' },
                  }}
                >
                  Reset Accrual
                </Button>
              )}
            </Box>
          )}

          {/* List */}
          {empLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ overflowY: 'auto', flex: 1, py: 0.5 }}>
              {filteredEmployees.map((emp) => {
                const isViewing = selectedEmp?.Emp_Code === emp.Emp_Code;
                const isChecked = checkedCodes.includes(emp.Emp_Code);
                return (
                  <Box
                    key={emp.Emp_Code}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      mx: 1,
                      my: 0.25,
                      borderRadius: 1.5,
                      transition: 'background-color 0.15s ease',
                      backgroundColor: isViewing
                        ? 'rgba(191,29,45,0.08)'
                        : isChecked
                          ? 'rgba(191,29,45,0.045)'
                          : 'transparent',
                      '&:hover': {
                        backgroundColor: isViewing ? 'rgba(191,29,45,0.12)' : 'rgba(0,0,0,0.04)',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 6,
                        bottom: 6,
                        width: 3,
                        borderRadius: 3,
                        backgroundColor: isViewing ? '#BF1D2D' : 'transparent',
                      },
                    }}
                  >
                    <Tooltip title={isChecked ? 'Deselect member' : 'Select member'} placement="left">
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        onChange={() => toggleChecked(emp.Emp_Code)}
                        sx={{ ml: 0.5, color: '#BF1D2D', '&.Mui-checked': { color: '#BF1D2D' } }}
                      />
                    </Tooltip>
                    <Box
                      role="button"
                      onClick={() => handleSelectEmployee(emp)}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.75,
                        pr: 1.5,
                        cursor: 'pointer',
                      }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          flexShrink: 0,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 700,
                          fontFamily: 'karla, sans-serif',
                          backgroundColor: isViewing ? '#BF1D2D' : 'grey.200',
                          color: isViewing ? '#fff' : 'text.secondary',
                          transition: 'background-color 0.15s ease',
                        }}
                      >
                        {(emp.First_name?.[0] || '') + (emp.Last_name?.[0] || '')}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontWeight: isViewing ? 700 : 500,
                            color: isViewing ? '#BF1D2D' : 'text.primary',
                            fontFamily: 'karla, sans-serif',
                          }}
                        >
                          {empLabel(emp)}
                        </Typography>
                        {emp.Badge_Number && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            #{emp.Badge_Number}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              {filteredEmployees.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">No members found</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Right: Summary */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!selectedEmp ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
              <PersonIcon sx={{ fontSize: 56, mb: 1.5, color: '#e0e0e0' }} />
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Select a member to view their accrual summary
              </Typography>
            </Box>
          ) : (
            <>
              {/* Employee header bar */}
              <Box sx={{
                px: 3,
                py: 1.75,
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}>
                <Box sx={{ width: 38, height: 38, borderRadius: '50%', backgroundColor: '#BF1D2D', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
                    {(selectedEmp.First_name?.[0] || '') + (selectedEmp.Last_name?.[0] || '')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, fontFamily: 'karla, sans-serif' }}>
                    {empLabel(selectedEmp)}
                    {selectedEmp.Badge_Number && (
                      <Chip label={`#${selectedEmp.Badge_Number}`} size="small" sx={{ ml: 1, verticalAlign: 'middle', height: 20, fontSize: '11px' }} />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Time Off &amp; Accruals</Typography>
                </Box>

                {availablePeriods.length > 0 && (
                  <Box sx={{ ml: 'auto' }}>
                    <Select
                      size="small"
                      value={selectedPeriod ?? ''}
                      onChange={(e) => handlePeriodChange(e.target.value)}
                      displayEmpty
                      sx={{
                        fontFamily: 'karla, sans-serif',
                        fontSize: 13,
                        minWidth: 165,
                        '& .MuiSelect-select': { py: 0.75 },
                      }}
                    >
                      <MenuItem value="" sx={{ fontFamily: 'karla, sans-serif', fontSize: 13, color: 'text.secondary' }}>
                        Current Period
                      </MenuItem>
                      {availablePeriods.map((period) => {
                        const isFuture = period > new Date().toISOString().slice(0, 10);
                        return (
                          <MenuItem key={period} value={period} sx={{ fontFamily: 'karla, sans-serif', fontSize: 13 }}>
                            {moment(period).format('MMM D, YYYY')}
                            {isFuture && (
                              <Chip
                                label="upcoming"
                                size="small"
                                sx={{ ml: 1, height: 16, fontSize: 10, backgroundColor: 'rgba(191,29,45,0.1)', color: '#BF1D2D' }}
                              />
                            )}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </Box>
                )}
              </Box>

              {/* Summary table */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
                {summaryLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : summary.length === 0 ? (
                  <Box sx={{ textAlign: 'center', pt: 8 }}>
                    <Typography color="text.secondary">No accrual summary data available.</Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#BF1D2D' }}>
                          {['Type', 'Start Date', 'Start', 'Carried',
                            ...(!hideSoldAccrued ? ['Accrued'] : []),
                            ...(!hideSoldAccrued && !compressed ? ['Sold'] : []),
                            'Used', 'Planned', 'Available',
                          ].map((h) => (
                            <TableCell key={h} sx={{ color: '#fff', fontWeight: 700, py: 1.25, fontFamily: 'karla, sans-serif', whiteSpace: 'nowrap' }}>
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.map((row, i) => (
                          <TableRow key={i} hover sx={{ '&:nth-of-type(even)': { backgroundColor: 'grey.50' } }}>
                            <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                            <TableCell>{row.date ? moment(row.date).format('MM/DD/YYYY') : '—'}</TableCell>
                            <TableCell>{row.start}</TableCell>
                            <TableCell>{row.carried}</TableCell>
                            {!hideSoldAccrued && (
                              <TableCell>
                                <ClickableCell value={row.accrued} list={row.accrued_list} type="Accrued" name={row.name} onDrilldown={setDrilldown} />
                              </TableCell>
                            )}
                            {!hideSoldAccrued && !compressed && <TableCell>{row.sold}</TableCell>}
                            <TableCell>
                              <ClickableCell value={row.used} list={row.used_list} type="Used" name={row.name} onDrilldown={setDrilldown} />
                            </TableCell>
                            <TableCell>
                              <ClickableCell value={row.planned} list={row.planned_list} type="Planned" name={row.name} onDrilldown={setDrilldown} />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: row.available < 0 ? '#BF1D2D' : 'inherit' }}>
                              {row.available}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </>
          )}
        </Box>
      </Paper>

      <DrilldownDialog drilldown={drilldown} onClose={() => setDrilldown(null)} />

      {canRevert && (
        <ResetAccrualDialog
          open={resetDialogOpen}
          onClose={() => !reverting && setResetDialogOpen(false)}
          subTypes={accrualSubTypes}
          onSubmit={handleResetSubmit}
          submitting={reverting}
        />
      )}
    </Box>
  );
};

export default MemberAccrualSummary;
